import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import type {
  ListArPaymentsInput,
  ListArPaymentsResponse,
  GetArPaymentResponse,
  ArPaymentListItem,
} from '@erp/rpc';

export function useArPaymentsList(filters: ListArPaymentsInput = {}) {
  const query = useQuery(
    orpc.accountsReceivable.arPayments.list.queryOptions({ input: filters }),
  );

  const payments = query.data?.payments ?? [];
  const pagination = query.data?.pagination;

  return {
    ...query,
    payments,
    pagination,
  } satisfies typeof query & {
    data?: ListArPaymentsResponse;
    payments: ArPaymentListItem[];
    pagination: ListArPaymentsResponse['pagination'] | undefined;
  };
}

export function useArPayment(id: string | undefined, enabled = true) {
  const query = useQuery({
    ...orpc.accountsReceivable.arPayments.get.queryOptions({
      input: id ? { id } : skipToken,
    }),
    enabled: Boolean(id) && enabled,
  });

  return {
    ...query,
    payment: query.data?.payment ?? null,
  } satisfies typeof query & {
    data?: GetArPaymentResponse;
    payment: GetArPaymentResponse['payment'] | null;
  };
}

function invalidatePaymentQueries(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  void queryClient.invalidateQueries({
    queryKey: orpc.accountsReceivable.arPayments.list.key(),
  });
  void queryClient.invalidateQueries({
    queryKey: orpc.accountsReceivable.arPayments.get.key(),
  });
}

/** Create a manually-entered payment. */
export function useCreateArPayment() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.arPayments.create.mutationOptions({
      onSuccess: () => invalidatePaymentQueries(queryClient),
    }),
  );
}

/** Edit a manual-origin payment. */
export function useUpdateArPayment() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.arPayments.update.mutationOptions({
      onSuccess: () => invalidatePaymentQueries(queryClient),
    }),
  );
}

/** Void / reverse / reactivate a manual-origin payment. */
export function useSetArPaymentStatus() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.arPayments.setStatus.mutationOptions({
      onSuccess: () => invalidatePaymentQueries(queryClient),
    }),
  );
}
